import {
    Box,
    Button,
    FormControl,
    FormLabel,
    HStack,
    Input,
    ListItem,
    Spinner,
    Text,
    UnorderedList,
    VStack,
} from '@chakra-ui/react';
import React from 'react';

export interface UploadedInvoiceImage {
    file: File;
    previewUrl: string;
    isTiff?: boolean;
}

export interface UploadedInvoicePdf {
    file: File;
    previewUrl: string;
    pageCount?: number;
}

export interface InvoiceFileDropzoneProps {
    isCompressing: boolean;
    isDragOver: boolean;
    fileInputRef: React.RefObject<HTMLInputElement>;
    uploadedImages: UploadedInvoiceImage[];
    uploadedPdf: UploadedInvoicePdf | null;
    onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onDragOver: (event: React.DragEvent) => void;
    onDragLeave: (event: React.DragEvent) => void;
    onDrop: (event: React.DragEvent) => void;
    onRemoveImage: (index: number) => void;
    onRemovePdf: () => void;
}

const InvoiceFileDropzone = ({
    isCompressing,
    isDragOver,
    fileInputRef,
    uploadedImages,
    uploadedPdf,
    onFileChange,
    onDragOver,
    onDragLeave,
    onDrop,
    onRemoveImage,
    onRemovePdf,
}: InvoiceFileDropzoneProps) => {
    return (
        <FormControl>
            <FormLabel>Invoice Images (JPG, PNG, or TIF)</FormLabel>
            <VStack spacing={4} align="stretch">
                <Box
                    border="2px dashed"
                    borderColor={isDragOver ? "blue.500" : "gray.300"}
                    borderRadius="md"
                    p={6}
                    textAlign="center"
                    cursor={isCompressing ? "wait" : "pointer"}
                    bg={isDragOver ? "blue.50" : "transparent"}
                    opacity={isCompressing ? 0.7 : 1}
                    pointerEvents={isCompressing ? "none" : "auto"}
                    transition="all 0.2s"
                    _hover={isCompressing ? undefined : {
                        borderColor: isDragOver ? "blue.500" : "blue.400",
                        bg: isDragOver ? "blue.50" : "blue.25"
                    }}
                    onClick={() => !isCompressing && fileInputRef.current?.click()}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                >
                    {isCompressing ? (
                        <>
                            <Spinner size="md" color="blue.500" mb={2} />
                            <Text color="gray.600" mb={2}>
                                Compressing Images…
                            </Text>
                        </>
                    ) : (
                        <>
                            <Text color="gray.600" mb={2}>
                                {isDragOver ? "Drop Images or a PDF Here" : "Click to Upload or Drag JPG, PNG, TIF Images or a PDF Here"}
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                                Multiple images or one PDF (multi-page PDFs and TIFFs are supported). Images are compressed automatically.
                            </Text>
                        </>
                    )}
                </Box>

                <Box
                    borderLeftWidth="3px"
                    borderLeftColor="blue.400"
                    bg="blue.50"
                    py={2}
                    px={3}
                    borderRadius="md"
                >
                    <Text fontSize="sm" fontWeight="semibold" color="gray.800" mb={1}>
                        For Reliable Extraction
                    </Text>
                    <UnorderedList spacing={1} fontSize="sm" color="gray.700" pl={1} mb={0}>
                        <ListItem>
                            <strong>Fill the frame:</strong> crop or rescan so the invoice page uses the image—avoid large
                            margins or empty space around the document.
                        </ListItem>
                        <ListItem>
                            <strong>Upright orientation:</strong> every page must be right-side up, not rotated sideways
                            or upside down.
                        </ListItem>
                    </UnorderedList>
                </Box>

                <Input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/tiff,image/tif,.tif,.tiff,application/pdf"
                    multiple
                    onChange={onFileChange}
                    display="none"
                    disabled={isCompressing}
                />

                {/* Uploaded PDF Preview */}
                {uploadedPdf && (
                    <VStack spacing={2} align="stretch">
                        <Text fontSize="sm" fontWeight="medium">
                            Uploaded PDF
                        </Text>
                        <HStack spacing={2}>
                            <Box
                                px={3}
                                py={2}
                                borderRadius="md"
                                border="1px solid"
                                borderColor="gray.200"
                                bg="red.50"
                                fontSize="sm"
                            >
                                PDF Document
                                {uploadedPdf.pageCount != null && uploadedPdf.pageCount > 0
                                    ? ` (${uploadedPdf.pageCount} ${uploadedPdf.pageCount === 1 ? 'page' : 'pages'})`
                                    : ''}
                            </Box>
                            <Button size="sm" colorScheme="red" variant="outline" onClick={onRemovePdf}>
                                Remove
                            </Button>
                        </HStack>
                    </VStack>
                )}

                {/* Uploaded Images Preview */}
                {uploadedImages.length > 0 && (
                    <VStack spacing={2} align="stretch">
                        <Text fontSize="sm" fontWeight="medium">
                            Uploaded Images ({uploadedImages.length})
                        </Text>
                        <HStack spacing={2} wrap="wrap">
                            {uploadedImages.map((image, index) => (
                                <Box
                                    key={index}
                                    position="relative"
                                    width="80px"
                                    height="80px"
                                    borderRadius="md"
                                    overflow="hidden"
                                    border="1px solid"
                                    borderColor="gray.200"
                                >
                                    {image.isTiff || !image.previewUrl ? (
                                        <Box
                                            width="100%"
                                            height="100%"
                                            bg="gray.100"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            px={1}
                                        >
                                            <Text fontSize="xs" color="gray.600" textAlign="center" noOfLines={2}>
                                                TIF
                                            </Text>
                                        </Box>
                                    ) : (
                                        <Box
                                            as="img"
                                            src={image.previewUrl}
                                            width="100%"
                                            height="100%"
                                            objectFit="cover"
                                            cursor="pointer"
                                            onClick={() => window.open(image.previewUrl, '_blank')}
                                        />
                                    )}
                                    <Box
                                        position="absolute"
                                        top="2px"
                                        right="2px"
                                        width="16px"
                                        height="16px"
                                        borderRadius="full"
                                        bg="red.500"
                                        color="white"
                                        fontSize="xs"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        cursor="pointer"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onRemoveImage(index);
                                        }}
                                        _hover={{ bg: "red.600" }}
                                    >
                                        ×
                                    </Box>
                                </Box>
                            ))}
                        </HStack>

                        {/* Disclaimer for multiple images */}
                        {uploadedImages.length > 1 && (
                            <Box
                                p={3}
                                bg="orange.50"
                                border="1px solid"
                                borderColor="orange.200"
                                borderRadius="md"
                                mt={2}
                            >
                                <HStack spacing={2} align="start">
                                    <Box color="orange.500" fontSize="sm" mt="1px">
                                        ⚠️
                                    </Box>
                                    <Text fontSize="sm" color="orange.800" fontWeight="medium">
                                        Important: Please ensure all uploaded images are from the same invoice.
                                        Images from different invoices may cause processing errors.
                                    </Text>
                                </HStack>
                            </Box>
                        )}
                    </VStack>
                )}
            </VStack>
        </FormControl>
    );
};

export default InvoiceFileDropzone;

